import { Avatar, type Breakpoint } from "@carrotfertility/carotene-core"
import { Skeleton } from "@internal/ui"
import React from "react"
import { formatProviderInitials } from "./formatting"
import { perViewport } from "./responsive"

/**
 * A rendered size in px, either flat or per viewport.
 *
 * The prop was a plain `number` until the 390px pass (CONAPP-271), which is where it stopped being
 * expressible: frame 9 draws every avatar on the page one step smaller than its desktop frame, so
 * each call site genuinely needs two values. Widened here rather than swapped for a semantic
 * `"row" | "feature"` union, because these are design pixel values and belong at the call site with
 * the design distinction they came from — and because a third role later is then a constant, not a
 * new union member every consumer has to learn.
 *
 * Deliberately NOT token-backed, and it cannot be: `theme.tokens` carries only `spacing`,
 * `borderRadius` and `borderWidth`, there is no avatar-size token, and `spacing` values are indices
 * into MUI's scale rather than lengths — `theme.spacing()` cannot produce 54px cleanly. The design
 * gives literal pixels, so literal pixels is the honest answer.
 */
type AvatarSize = number | Partial<Record<Breakpoint, number>>

/**
 * The four sizes the design draws, and the only place any of them is written down.
 *
 * `.av` / `.mob-lead img` — appointment rows, i.e. the Up next card and the Past rows. The default,
 * so neither call site passes a size at all.
 */
const ROW_AVATAR_SIZE: AvatarSize = perViewport(48, 54)

/**
 * `.empty-avs img` / `.mob-avs img` and `.comp img` / `.mob-comp img` — the booking card's three
 * faces and the Carrot Companion portrait. One step up from a row at both viewports.
 */
export const FEATURE_AVATAR_SIZE: AvatarSize = perViewport(64, 72)

export function AppointmentsAvatar({
  name,
  photoUrl,
  alt,
  size = ROW_AVATAR_SIZE
}: {
  name: string
  photoUrl: string | null
  alt?: string
  /** Defaults to the appointment-row size. Pass `FEATURE_AVATAR_SIZE` for the larger blocks. */
  size?: AvatarSize
}): JSX.Element {
  // Passed straight through to `sx`, which resolves a breakpoint object natively — so the responsive
  // case needs no `useMediaQuery` and costs this component nothing.
  //
  // Load-bearing ordering: `inlineSize`/`blockSize` are emitted AFTER MUI Avatar's own `width: 40px`
  // / `height: 40px`, which is the only reason they win the cascade. `AppointmentsAvatar.test.tsx`
  // pins it with `cssForRenderedClasses`, because `getComputedStyle` reads empty for anything MUI
  // emits inside a media query and jsdom never evaluates one.
  const sizing = { inlineSize: size, blockSize: size }

  if (photoUrl) {
    return <Avatar variant="rounded" src={photoUrl} alt={alt ?? name} sx={sizing} />
  }

  // The design's initials avatar (`.av-i`) is white on `secondary.dark`.
  return (
    <Avatar
      variant="rounded"
      sx={(theme) => ({
        ...sizing,
        backgroundColor: theme.palette.secondary.dark,
        color: theme.palette.secondary.contrastText
      })}
    >
      {formatProviderInitials(name)}
    </Avatar>
  )
}

/** A placeholder at the size `AppointmentsAvatar` renders, so a loading frame keeps its row height. */
export function AppointmentsAvatarSkeleton(): JSX.Element {
  return <Skeleton sx={{ inlineSize: ROW_AVATAR_SIZE, blockSize: ROW_AVATAR_SIZE }} />
}
